/**
 * Vitality dynamics.
 *
 *   v(t+1) = clamp( v(t) * e^(-λΔt) + α·dwell + β·reuse )
 *
 * Exponential decay over time, reinforced by dwell and reuse. Three tunable
 * constants, all carried on the active Regime. High-vitality fragments are sharp
 * and legible; decaying ones lose specifics and drift toward slogan.
 */
export function clamp01(x) {
    return x < 0 ? 0 : x > 1 ? 1 : x;
}
/**
 * Apply time-based decay up to `now`, returning the decayed vitality without
 * mutating the fragment. Δt is derived from `vitalityUpdatedAt`.
 */
export function decayedVitality(f, now, regime) {
    const dtSeconds = Math.max(0, (now - f.vitalityUpdatedAt) / 1000);
    const decayed = f.vitality * Math.exp(-regime.vitality.decayPerSecond * dtSeconds);
    return clamp01(decayed);
}
/**
 * Advance a fragment's vitality to `now`, folding in any newly accrued dwell
 * (seconds) and reuse (count) since the last update. Returns a new Fragment.
 */
export function reinforce(f, now, regime, opts = {}) {
    const dwellSeconds = opts.dwellSeconds ?? 0;
    const reuseEvents = opts.reuseEvents ?? 0;
    const base = decayedVitality(f, now, regime);
    const gained = regime.vitality.dwellGain * dwellSeconds + regime.vitality.reuseGain * reuseEvents;
    return {
        ...f,
        vitality: clamp01(base + gained),
        dwellTotal: f.dwellTotal + dwellSeconds,
        reuseCount: f.reuseCount + reuseEvents,
        vitalityUpdatedAt: now,
    };
}
/** Batch-decay a set of fragments to `now` (no reinforcement). */
export function decayAll(fragments, now, regime) {
    return fragments.map((f) => ({
        ...f,
        vitality: decayedVitality(f, now, regime),
        vitalityUpdatedAt: now,
    }));
}
/** A fragment is considered dead (removable from the live field) below this. */
export const DEATH_THRESHOLD = 0.04;
export function isDead(f) {
    return f.vitality < DEATH_THRESHOLD;
}
//# sourceMappingURL=vitality.js.map